import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Alert from 'flarum/common/components/Alert';
import CreditExchangeModal from './CreditExchangeModal';

// DEV BUILD MARKER - 7c4c8b2-do-not-remove
// This comment proves the compiled JS contains new code from the July 19 build
// If you see this text in forum.js, the update was successful

export default class CourseLibraryPage extends IndexPage {
  static isPage = true;

  oninit(vnode) {
    Page.prototype.oninit.call(this, vnode);
    this.bodyClass = 'App--index';
    this.levels = [];
    this.courses = [];
    this.activeLevelCode = null;
    this.loading = true;
    this.error = null;

    this.loadData();
  }

  // 静默请求：不让 Flarum 全局 error handler 弹权限提示
  silentRequest(opts) {
    return app.request({
      ...opts,
      errorHandler: () => {},
    });
  }

  async loadData() {
    this.loading = true;
    this.error = null;

    try {
      const apiUrl = app.forum.attribute('apiUrl');

      const [levelsRes, coursesRes] = await Promise.all([
        this.silentRequest({
          method: 'GET',
          url: `${apiUrl}/taro/cms/levels`,
        }),
        this.silentRequest({
          method: 'GET',
          url: `${apiUrl}/taro/cms/courses`,
        }),
      ]);

      this.levels = app.store.pushPayload(levelsRes) || [];
      this.courses = app.store.pushPayload(coursesRes) || [];
    } catch (e) {
      this.error = e.message || '加载课程列表失败';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  get filteredCourses() {
    if (!this.activeLevelCode) return this.courses;
    return this.courses.filter((c) => c.levelCode() === this.activeLevelCode);
  }

  // ─── 侧边导航栏 ──────────────────────────────

  hero() {
    const hidden = app.forum.attribute('taroCourseLibraryHeroHidden') || false;
    if (hidden) return null;
    const subtitle = app.forum.attribute('taroCourseLibraryHeroSubtitle') || '系统学习格林塔罗知识体系';
    const icon = app.forum.attribute('taroCourseLibraryHeroIcon') || 'fas fa-book-reader';
    const color = app.forum.attribute('taroCourseLibraryHeroColor') || '';
    return (
      <header className="Hero" style={color ? { '--hero-bg': color } : undefined}>
        <div className="container">
          <h1 className="Hero-title">
            <i className={icon} style={{ marginRight: '8px' }} />
            课程中心
          </h1>
          {subtitle ? <div className="Hero-subtitle">{subtitle}</div> : null}
        </div>
      </header>
    );
  }

  contentItems() {
    const items = new ItemList();
    items.add('courseContent', this.pageContent(), 100);
    return items;
  }

  // ─── 页面主体内容 ─────────────────────────────

  pageContent() {
    if (this.loading) {
      return <LoadingIndicator />;
    }

    if (this.error) {
      const isAuthError = this.error.includes('401') || this.error.includes('403') || this.error.includes('permission') || this.error.includes('权限');
      return (
        <div className="CourseLibraryPage">
          <div className="CourseLibraryPage-error">
            {isAuthError ? (
              <div className="CourseLibraryPage-error-auth">
                <i className="fas fa-user-lock" />
                <h2>请先登录</h2>
                <p>您需要登录才能浏览课程内容</p>
                <Button className="Button Button--primary" onclick={() => app.session.loginModal.show()}>
                  立即登录
                </Button>
              </div>
            ) : (
              <div className="CourseLibraryPage-error-msg">
                <i className="fas fa-exclamation-triangle" />
                <p>{this.error}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="CourseLibraryPage">
        {/* Header */}
        <div className="CourseLibraryPage-header">
          <h1 className="CourseLibraryPage-title">
            课程中心
            <span className="version-badge">v2026-07-19</span>
          </h1>
          <Button className="Button CourseLibraryPage-exchangeBtn" onclick={() => this.openExchangeModal()}>
            <i className="fas fa-exchange-alt" /> 星币兑换宝剑
          </Button>
        </div>

        {/* Level tabs */}
        <div className="CourseLibraryPage-levelTabs">
          <button
            className={'CourseLibraryPage-levelTab' + (this.activeLevelCode === null ? ' active' : '')}
            onclick={() => { this.activeLevelCode = null; m.redraw(); }}
          >
            全部
          </button>
          {this.levels.map((level) => (
            <button
              className={'CourseLibraryPage-levelTab' + (this.activeLevelCode === level.code() ? ' active' : '')}
              onclick={() => { this.activeLevelCode = level.code(); m.redraw(); }}
            >
              {level.name()}
            </button>
          ))}
        </div>

        {/* Course grid or empty */}
        {this.filteredCourses.length > 0 ? (
          <div className="CourseLibraryPage-courseGrid">
            {this.filteredCourses.map((course) => this.renderCourseCard(course))}
          </div>
        ) : (
          <div className="CourseLibraryPage-empty">
            <i className="fas fa-book-open" />
            <p>暂无课程</p>
          </div>
        )}
      </div>
    );
  }



  renderCourseCard(course) {
    const cover = course.coverImage();
    const difficulty = course.difficulty() || 'beginner';
    const articleCount = course.articleCount() || 0;
    const swordCost = course.swordCost() || 0;
    const status = course.status();

    const difficultyLabel = { beginner: '入门', intermediate: '进阶', advanced: '高级' };
    const difficultyIcon = { beginner: 'seedling', intermediate: 'star', advanced: 'fire' };

    return (
      <div
        className="CourseLibraryPage-courseCard"
        onclick={() => m.route.set(app.route('courseDetail', { id: course.id() }))}
      >
        {/* Cover */}
        {cover ? (
          <img className="CourseLibraryPage-courseCard-cover" src={cover} alt={course.title()} />
        ) : (
          <div className="CourseLibraryPage-courseCard-cover no-cover">
            <i className="fas fa-graduation-cap" />
          </div>
        )}

        {/* Body */}
        <div className="CourseLibraryPage-courseCard-body">
          <h3 className="CourseLibraryPage-courseCard-title">{course.title()}</h3>
          {course.description() && (
            <p className="CourseLibraryPage-courseCard-desc">{course.description()}</p>
          )}
          <div className="CourseLibraryPage-courseCard-meta">
            <span>
              <i className={'fas fa-' + difficultyIcon[difficulty] || 'fa-circle'} />
              <span className={'CourseLibraryPage-courseCard-difficulty ' + difficulty}>
                {difficultyLabel[difficulty] || difficulty}
              </span>
            </span>
            <span>
              <i className="fas fa-file-alt" /> {articleCount} 篇文章
            </span>
            {swordCost > 0 && (
              <span>
                <i className="fas fa-shield-alt" /> {swordCost} 宝剑
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="CourseLibraryPage-courseCard-footer">
          {status === 'published' ? (
            <Button
              className="Button Button--primary"
              onclick={(e) => {
                e.stopPropagation();
                m.route.set(app.route('courseDetail', { id: course.id() }));
              }}
            >
              {swordCost > 0 ? (
                <span><i className="fas fa-eye" /> 查看</span>
              ) : (
                <span><i className="fas fa-play" /> 免费学习</span>
              )}
            </Button>
          ) : (
            <span className="btn-purchased">
              <i className="fas fa-clock" /> 即将开放
            </span>
          )}
        </div>
      </div>
    );
  }
}
